import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Modal, Select } from '@components/common';
import Table from '@components/common/Table';
import AlarmManagementForm from '@components/ModalComponents/AlarmManagementForm';
import FileTypeSelectionForm from '@components/ModalComponents/FileTypeSelectionForm';

import {
	useMutateExportAlarmManagementOran,
} from '@hooks/alarms/alarmManagamentData/useMutateExportAlarmManagementOran';
import { useMutateUpdateOranAlarm } from '@hooks/alarms/alarmManagamentData/useMutateUpdateOranAlarm';
import { useQueryAlarmOran } from '@hooks/alarms/alarmManagamentData/useQueryAlarmOran';
import { useQueryAlarmsList } from '@hooks/alarms/alarmManagamentData/useQueryAlarmsList';
import { useAlarmManagementFilters } from '@hooks/alarms/alarmManagementFilters/useAlarmManagementFilters';
import { useMutateUpdateAlarm } from '@hooks/alarms/useMutateUpdateAlarm';
import { useQueryAlarmVendors } from '@hooks/alarms/useQueryAlarmVendors';
import { useWindowHeight } from '@hooks/utils/useWindowHeight';

import { IAlarmManagement } from '@interfaces/alarms/alarm';
import { IAlarmSettingsDTO } from '@interfaces/alarms/dto/alarmsDTO';
import { IExportDefaultDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import {
	defaultMeta,
	EVendorsValues,
	TMenuActionsTypes,
	TVendorsValues,
} from '@enums/common';

import {
	exportRadioButtonsData,
	menuActionWithoutSelection,
} from '@constants/common';
import { MODAL_WIDTH_480 } from '@constants/modals';
import { CUSTOM_ALARM_ID, getLimenetFormFields, getOranFormFields } from './constants';

import { getAlarmManagementColumns } from './columns';
import { generateAlarmsFilters, getAlarmFilterValues } from './utils';

import styles from './AlarmManagementByVendor.module.scss';

const WINDOW_HEIGHT_OFFSET = 278;

const AlarmManagementByVendor = () => {
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');
	const [activeExportAction, setActiveExportAction] = useState<TMenuActionsTypes | undefined>();
	const [selectedAlarmsIds, setSelectedAlarmsIds] = useState<string[] | null>(null);
	const [editableAlarm, setEditableAlarm] = useState<IAlarmManagement>();
	const [selectedGeneratedFilters, setSelectedGeneratedFilters] = useState<Record<string, TSelectOption>>({});
	const [selectedVendor, setSelectedVendor] = useState<TSelectOption<TVendorsValues>>(null);

	const [isAlarmManagementFormModal, setIsAlarmManagementFormModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);

	const { alarmVendorsOptions } = useQueryAlarmVendors();

	const vendor = selectedVendor?.value;
	const isOranVendor = vendor === EVendorsValues.oran;
	const isSelectShown = alarmVendorsOptions.length > 1;

	const filterValues = useMemo(() => {
		return getAlarmFilterValues(selectedGeneratedFilters);
	}, [selectedGeneratedFilters]);

	const {
		alarmList,
		countOfRecords,
		isLoadingTable,
	} = useQueryAlarmsList({
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		vendor: vendor!,
		...filterValues,
	});

	const { alarm } = useQueryAlarmOran(isOranVendor ? editableAlarm?.id : undefined);

	const { filtersList, isLoadingFilters } = useAlarmManagementFilters(vendor!);

	const isLoading = isLoadingFilters || isLoadingTable;

	const clearPagination = useCallback(() => {
		setPage(defaultMeta.page);
		setPageSize(defaultMeta.pageSize);
	}, []);

	useEffect(() => {
		if (!selectedVendor && alarmVendorsOptions.length) {
			setSelectedVendor(alarmVendorsOptions[0]);
		}
	}, [selectedVendor, alarmVendorsOptions]);

	useEffect(() => {
		setSelectedGeneratedFilters({});
		clearPagination();
		setSortDirection(false);
		setSortFieldName(null);
		setEditableAlarm(undefined);
		setSearchValue('');
	}, [vendor, clearPagination]);

	useEffect(() => {
		clearPagination();
	}, [filterValues, clearPagination]);

	const openEditAlarmForm = useCallback((alarm: IAlarmManagement) => {
		setEditableAlarm(alarm);
		setIsAlarmManagementFormModal(true);
	}, []);

	const closeEditAlarmForm = useCallback(() => {
		setEditableAlarm(undefined);
		setIsAlarmManagementFormModal(false);
	}, []);

	const toggleFileTypeSelectionModal = useCallback(() => {
		setIsOpenFileTypeSelectionModal(prevState => !prevState);
	}, []);

	const closeTypeSelectionForm = useCallback(() => setIsOpenFileTypeSelectionModal(false), []);

	const { editAlarm, isLoadingEditAlarm } = useMutateUpdateAlarm([closeEditAlarmForm]);
	const { editOranAlarm, isLoadingEditOranAlarm } = useMutateUpdateOranAlarm(closeEditAlarmForm);
	const { handlePreparingExport, isLoadingExportAlarmManagementOran } = useMutateExportAlarmManagementOran();

	const handleEditOranAlarm = useCallback((data: IAlarmSettingsDTO) => {
		void editOranAlarm(data, alarm);
	}, [editOranAlarm, alarm]);

	const activeExport = useCallback((data: IExportDefaultDTO) => {
		handlePreparingExport(data);
	}, [handlePreparingExport]);

	const handleExport = useCallback((alarmsIds: Array<string>, actionId: TMenuActionsTypes) => {
		setSelectedAlarmsIds(alarmsIds);
		setActiveExportAction(actionId);

		toggleFileTypeSelectionModal();
	}, [toggleFileTypeSelectionModal]);

	const columns = useMemo(() => {
		return getAlarmManagementColumns({ vendor: vendor!, openEditAlarmForm });
	}, [vendor, openEditAlarmForm]);

	const onVendorChange = useCallback((option: TSelectOption<TVendorsValues>) => {
		setSelectedVendor(option);
	}, []);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const filters: TTableFilter[] = useMemo(() => {
		return generateAlarmsFilters(
			filtersList,
			selectedGeneratedFilters,
			setSelectedGeneratedFilters,
		);
	}, [filtersList, selectedGeneratedFilters]);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: searchValue,
		name: 'searchValue',
		handler: searchHandler,
		tooltipText: 'Search',
	}], [searchValue, searchHandler]);

	const formFields = isOranVendor ? getOranFormFields(editableAlarm) : getLimenetFormFields(editableAlarm);

	const isCustomAlarm = isOranVendor && (alarm?.alarmId as any)?.name === CUSTOM_ALARM_ID;

	const windowHeight = useWindowHeight();

	const [tableMaxHeight, setTableMaxHeight] = useState('');

	useEffect(() => {
		const maxHeight = windowHeight - WINDOW_HEIGHT_OFFSET;

		setTableMaxHeight(`${maxHeight}px`);
	}, [windowHeight]);

	return (
		<>
			<Modal
				confirmQuestion='Are you sure you want to close the window?'
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isAlarmManagementFormModal}
				onClose={closeEditAlarmForm}
				title='Edit alarm notification rule'
				width='815px'
			>
				{(alarm || editableAlarm) && (
					<AlarmManagementForm
						editAlarm={isOranVendor ? handleEditOranAlarm : editAlarm}
						editableAlarm={(isOranVendor ? alarm : editableAlarm) as IAlarmManagement}
						fields={formFields}
						isCustomAlarm={isCustomAlarm}
						isLoadingEditAlarm={isOranVendor ? isLoadingEditOranAlarm : isLoadingEditAlarm}
						onClose={closeEditAlarmForm}
						setIsDirtyFormModal={setIsDirtyFormModal}
					/>
				)}
			</Modal>
			{isOpenFileTypeSelectionModal && (
				<Modal
					isOpen={isOpenFileTypeSelectionModal}
					onClose={toggleFileTypeSelectionModal}
					title='File extension to export'
					width={MODAL_WIDTH_480}
				>
					<FileTypeSelectionForm
						activeGroupTableAction={activeExportAction}
						closeModals={closeTypeSelectionForm}
						exportRadioButtonsData={exportRadioButtonsData}
						filter={{
							...filterValues,
							page,
							pageSize,
							sortDirection,
							sortFieldName,
							searchValue,
						}}
						handlePreparingExport={activeExport}
						isLoading={isLoadingExportAlarmManagementOran}
						selectedIds={selectedAlarmsIds}
						toggleModal={toggleFileTypeSelectionModal}
					/>
				</Modal>

			)}
			<div className={styles.alarmManagementTableContainer}>
				{isSelectShown && (
					<div className={styles.selectWrapper}>
						<Select
							disabled={!isSelectShown}
							handleChange={onVendorChange}
							isWithoutUseForms
							label='Vendor'
							name='vendor'
							options={alarmVendorsOptions}
							placeholder='Select vendor'
							value={selectedVendor}
							width='240px'
						/>
					</div>
				)}
				<Table
					columns={columns}
					customExportActions={menuActionWithoutSelection}
					data={alarmList}
					filters={filters}
					handleExport={handleExport}
					isExport={isOranVendor}
					isLoading={isLoading}
					isPinColumns
					isWithoutCheckbox
					isWithoutDeleteAction
					maxHeight={tableMaxHeight}
					pagination={pagination}
					search={search}
					sort={sort}
					stickyLeftColumnIndexes={[0, 1]}
					tableName={vendor}
				/>
			</div>
		</>
	);
};

export default memo(AlarmManagementByVendor);
